import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-void grid-bg flex items-center justify-center px-5">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex flex-col items-center mb-8">
          <Logo size="text-2xl" />
          <span className="mt-1 text-[10px] tracking-wide text-white/45 whitespace-nowrap text-center">
            AI apps, agents &amp; websites — built fast.
          </span>
        </Link>
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="rounded-2xl border border-neon-cyan/20 bg-surface p-8 shadow-[0_0_40px_rgba(0,255,156,0.1)]"
        >
          <div className="flex items-center gap-2 mb-6">
            <Lock size={18} className="text-neon-cyan" />
            <h1 className="font-heading font-bold text-xl">Admin Access</h1>
          </div>

          {error && (
            <div data-testid="login-error" className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-white/60">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-void border-white/15 mt-1" required />
            </div>
            <div>
              <Label className="text-xs text-white/60">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-void border-white/15 mt-1" required />
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="neon-btn w-full bg-neon-cyan text-black font-semibold py-3 rounded-xl mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Log in
          </button>
        </form>
        <Link to="/" className="block text-center mt-6 text-xs text-white/40 hover:text-neon-cyan transition-colors">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
